import { FC, Fragment, useEffect } from "react";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import Layout from '../../Layout/index';
import Policies from "../Terms_Conditions/Policies";
import Terms from "../Terms_Conditions/Terms";
import Auth from "../Auth";
import Screens from "./tabs";
import Topup from "../Profile/Topup";
import Editprofile from "../Profile/Editprofile";
import Deposits from "../Profile/Deposits";
import CheckDeposit from "../Profile/CheckDeposit";
import Tables from "../Home/tables";
import Home from "../Home";
import Notifications from "../Notifications";
import Devices from "../Devices";
import Clients from "../Clients";
import AllUsers from "../AllUsers";
import Games from "../Games";
import Revenue from "../Revenue";
import Products from "../Products";
import Spaces from "../Spaces";
import Orders from "../Orders";
import GadgetOrders from "../GadgetOrders";
import Installers from "../Installers";
import MarketplaceStaff from "../MarketplaceStaff";
import Firmware from "../Firmware";
import Events from "../Events";
import Sponsors from "../Sponsors";
import Profile from "../Profile";
import { Connect, mapDispatchToProps, mapStateToProps } from "../../Redux";
import eventBus from "../../configs/eventsBus";
import Loader from "./loader";
import AlertDialogSlide from "./alert";
import { toast, ToastContainer } from 'react-toastify';
import { Api } from "../../helpers";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../configs";
import { UserProps } from "../../types/user";

// Table QR codes (the gadget's TFT and the app's place QR) encode
// https://spotpool.co?placeId=<place>&tableId=<table>. Scanned with a phone's own camera that just
// opens this site at "/", so the link is remembered here until the user is signed in and then
// resolved the same way the in-site scanner does it (Home's onScan -> checkForPlace -> /tables).
const PENDING_SCAN_KEY = 'pendingTableScan'
const readPendingScan = () => { try { return sessionStorage.getItem(PENDING_SCAN_KEY) } catch { return null } }
const writePendingScan = (url: string | null) => {
    try {
        if (url) sessionStorage.setItem(PENDING_SCAN_KEY, url)
        else sessionStorage.removeItem(PENDING_SCAN_KEY)
    } catch { }
}

// A phone that opened a table QR link here doesn't have the app (with it installed, App Links /
// Universal Links open the app instead - see the app's helpers/deepLink.ts) - send it to the
// store rather than the web sign-in. Desktop/tablet browsers, and iPhones until APP_STORE_URL is
// set, keep the web flow below.
const storeUrlForThisDevice = () => {
    const ua = navigator.userAgent || ''
    if (/android/i.test(ua)) return PLAY_STORE_URL
    if (/iphone|ipod/i.test(ua)) return APP_STORE_URL || null
    return null
}

interface Props {
    loggedIn?: boolean
    logOut?: () => void
    setRedux?: (data: any) => void
    User?: UserProps
}

const Routes0: FC<Props> = ({
    loggedIn,
    User,
    logOut,
    setRedux
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const appScreenRoutes = (
        <Route element={<Screens />}>
            <Route path="/home" element={<Home />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/users" element={<AllUsers />} />
            <Route path="/games" element={<Games />} />
            <Route path="/revenue" element={<Revenue />} />
            <Route path="/products" element={<Products />} />
            <Route path="/spaces" element={<Spaces />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/gadgetOrders" element={<GadgetOrders />} />
            <Route path="/installers" element={<Installers />} />
            <Route path="/marketplaceStaff" element={<MarketplaceStaff />} />
            <Route path="/firmware" element={<Firmware />} />
            <Route path="/events" element={<Events />} />
            <Route path="/sponsors" element={<Sponsors />} />
            <Route path="/profile" element={<Profile />} />
        </Route>
    );
    const getUserData = () => {
        Api({
            controller: 'Users',
            action: 'getUserData'
        }).then((user: any) => {
            if (setRedux)
                setRedux({ User: { ...User, ...user } })
        })
    }
    useEffect(() => {
        if (new URLSearchParams(location.search).get('placeId')) {
            const storeUrl = storeUrlForThisDevice()
            if (storeUrl) {
                window.location.replace(storeUrl)
                return
            }
            // Built from the router's location (not window.location), which a sibling redirect
            // may already have rewritten by the time this effect runs.
            writePendingScan(window.location.origin + location.pathname + location.search)
        }
        const pending = readPendingScan()
        if (!pending) return
        if (!loggedIn) {
            // Auth navigates to /home on sign-in, which re-runs this effect (loggedIn changes) and
            // picks the saved link back up.
            if (location.pathname !== '/auth') navigate('/auth', { replace: true })
            return
        }
        writePendingScan(null)
        const tableId = new URL(pending).searchParams.get('tableId')
        Api({
            controller: 'Users',
            action: 'checkForPlace',
            qrValue: pending
        }).then((resp: any) => {
            if (resp?.error) {
                toast.error(resp.error)
                navigate('/home', { replace: true })
                return
            }
            navigate('/tables', {
                replace: true,
                state: {
                    _id: resp.placeId,
                    isCustomer: true,
                    pricePerGame: resp.placeTokenPrice,
                    scanned: true,
                    tableId
                }
            })
        }).catch(() => {
            toast.error('Could not open the scanned table. Please try again.')
            navigate('/home', { replace: true })
        })
    }, [loggedIn, location.search])

    useEffect(() => {
        eventBus.on('routes', (info: any) => {
            let { action, type, message } = info.detail
            if (logOut && action === 'logOut') {
                logOut()
                // '/' is the marketing landing page (Layout/index.tsx's Hero/About/Pricing/...),
                // not the sign-in screen - a signed-out user should land on /auth directly rather
                // than having to find "Sign In" again from the landing page.
                navigate('/auth', { replace: true })
            }
            else if (action === 'toastMsg')
                (toast as any)[type](message)
            else if (action === 'getUserData')
                getUserData()
        })
        return () => {
            eventBus.remove('routes',{})
        }
    }, [])

    return (
        <Fragment>
            <Routes>
                <Route path="/" element={<Layout />} />
                {/* Table QR links (https://spotpool.co/t?placeId=..&tableId=..) - handled by the
                    effect above, which redirects away; the landing page just shows meanwhile. */}
                <Route path="/t" element={<Layout />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="/terms_and_conditions" element={<Terms />} />
                {!loggedIn && <Route path="/auth" element={<Auth />} />}
                {loggedIn && (
                    <>
                        <Route path="/topup" element={<Topup />} />
                        <Route path="/edit_profile" element={<Editprofile />} />
                        <Route path="/deposits" element={<Deposits />} />
                        <Route path="/check_deposit" element={<CheckDeposit />} />
                        <Route path="/tables" element={<Tables />} />
                    </>
                )}
                {appScreenRoutes}
                <Route path="*" element={<Navigate to={loggedIn ? "/home" : "/"} />} />
            </Routes>
            <Loader />
            <AlertDialogSlide />
            <ToastContainer />
        </Fragment>
    );
}

export default Connect(mapStateToProps, mapDispatchToProps)(Routes0);
